/**
 * NotesEditor component - TipTap-based notes editor.
 */

import { useEditor, EditorContent } from "@tiptap/react";
import type { JSONContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import UniqueID from "@tiptap/extension-unique-id";
import Underline from "@tiptap/extension-underline";
import { useEffect, useRef, useCallback, useState } from "react";
import { treeToTiptap } from "../../lib/treeConverter";
import type { TreeNode } from "../../lib/types";
import type { NodeChangeData } from "../../extensions/GlobalDragHandle";
import {
  Undo2,
  Redo2,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  Heading1,
  Heading2,
  Heading3,
  Heading4,
  ChevronDown,
  List,
} from "lucide-react";
import GlobalDragHandle from "../../extensions/GlobalDragHandle";
import HideEmptySections from "../../extensions/HideEmptySections";
import CustomHeading from "../../extensions/CustomHeading";
import CustomListItem from "../../extensions/CustomListItem";
import CustomParagraph from "../../extensions/CustomParagraph";
import type { Editor } from "@tiptap/react";

// =============================================================================
// Toolbar Components
// =============================================================================

interface ToolbarButtonProps {
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
  title: string;
  children: React.ReactNode;
}

function ToolbarButton({ onClick, disabled, active, title, children }: ToolbarButtonProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-active={active || undefined}
      className="toolbar-button"
      title={title}
    >
      {children}
    </button>
  );
}

const headingLevels = [
  { level: 1, label: "Heading 1", icon: Heading1 },
  { level: 2, label: "Heading 2", icon: Heading2 },
  { level: 3, label: "Heading 3", icon: Heading3 },
  { level: 4, label: "Heading 4", icon: Heading4 },
] as const;

function HeadingDropdown({ editor }: { editor: Editor }) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeLevel = headingLevels.find((h) =>
    editor.isActive("heading", { level: h.level })
  );

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="heading-dropdown" ref={dropdownRef}>
      <button
        className="toolbar-button heading-dropdown-trigger"
        data-active={activeLevel ? true : undefined}
        title="Heading"
        onClick={() => setOpen(!open)}
      >
        {activeLevel ? <activeLevel.icon size={16} /> : <Heading1 size={16} />}
        <ChevronDown size={10} />
      </button>
      {open && (
        <div className="heading-dropdown-menu">
          {headingLevels.map(({ level, label, icon: Icon }) => (
            <button
              key={level}
              className="heading-dropdown-item"
              data-active={editor.isActive("heading", { level }) || undefined}
              onClick={() => {
                editor.chain().focus().toggleHeading({ level }).run();
                setOpen(false);
              }}
            >
              <Icon size={16} />
              <span>{label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

interface EditorToolbarProps {
  editor: Editor | null;
}

function EditorToolbar({ editor }: EditorToolbarProps) {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => forceUpdate((n) => n + 1);

    editor.on("selectionUpdate", handleUpdate);
    editor.on("transaction", handleUpdate);

    return () => {
      editor.off("selectionUpdate", handleUpdate);
      editor.off("transaction", handleUpdate);
    };
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="notes-toolbar" role="toolbar" aria-label="toolbar">
      <div className="toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
          title="Undo (Ctrl+Z)"
        >
          <Undo2 size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
          title="Redo (Ctrl+Shift+Z)"
        >
          <Redo2 size={16} />
        </ToolbarButton>
      </div>

      <div className="toolbar-separator" />

      <div className="toolbar-group">
        <HeadingDropdown editor={editor} />
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive("bulletList")}
          title="Bullet List"
        >
          <List size={16} />
        </ToolbarButton>
      </div>

      <div className="toolbar-separator" />

      <div className="toolbar-group">
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive("bold")}
          title="Bold (Ctrl+B)"
        >
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive("italic")}
          title="Italic (Ctrl+I)"
        >
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive("underline")}
          title="Underline (Ctrl+U)"
        >
          <UnderlineIcon size={16} />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive("strike")}
          title="Strikethrough"
        >
          <Strikethrough size={16} />
        </ToolbarButton>
      </div>
    </div>
  );
}

// =============================================================================
// Styles
// =============================================================================

const styles: Record<string, React.CSSProperties> = {
  editor: {
    flex: 1,
    overflow: "auto",
    padding: "16px",
  },
};

// Inject editor styles
const injectStyles = () => {
  const styleId = "notes-editor-styles";
  if (document.getElementById(styleId)) return;

  const style = document.createElement("style");
  style.id = styleId;
  style.textContent = `
    /* Editor container */
    .notes-editor-container {
      display: flex;
      flex-direction: column;
      height: 100%;
    }
    /* Toolbar styles */
    .notes-toolbar {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 8px 12px;
      border-bottom: 1px solid #e5e7eb;
      background: #fafafa;
    }
    .toolbar-group {
      display: flex;
      align-items: center;
      gap: 2px;
    }
    .toolbar-separator {
      width: 1px;
      height: 20px;
      background: #e5e7eb;
      margin: 0 8px;
    }
    /* Toolbar button styles */
    .notes-toolbar .toolbar-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border: none;
      background: transparent;
      border-radius: 6px;
      cursor: pointer;
      color: #6b7280;
      transition: background 0.15s, color 0.15s;
    }
    .notes-toolbar .toolbar-button:hover:not(:disabled) {
      background: #e5e7eb;
      color: #374151;
    }
    .notes-toolbar .toolbar-button:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    .notes-toolbar .toolbar-button[data-active] {
      background: #dbeafe;
      color: #2563eb;
    }
    .notes-toolbar .toolbar-button[data-active]:hover {
      background: #bfdbfe;
    }
    /* Heading dropdown */
    .heading-dropdown {
      position: relative;
    }
    .heading-dropdown-trigger {
      width: auto !important;
      gap: 2px;
      padding: 0 6px !important;
    }
    .heading-dropdown-menu {
      position: absolute;
      top: 100%;
      left: 0;
      margin-top: 4px;
      background: white;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
      padding: 4px;
      z-index: 50;
      min-width: 140px;
    }
    .heading-dropdown-item {
      display: flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      padding: 6px 8px;
      border: none;
      background: transparent;
      border-radius: 6px;
      cursor: pointer;
      color: #374151;
      font-size: 13px;
      white-space: nowrap;
    }
    .heading-dropdown-item:hover {
      background: #f3f4f6;
    }
    .heading-dropdown-item[data-active] {
      background: #dbeafe;
      color: #2563eb;
    }
    .notes-editor .ProseMirror {
      outline: none;
      min-height: 200px;
      padding-left: 40px;
      padding-right: 40px;
    }
    /* Drag handle styles */
    .drag-handle {
      position: fixed;
      display: flex;
      align-items: center;
      gap: 0;
      height: 20px;
      z-index: 50;
      cursor: grab;
    }
    .drag-handle.hide {
      display: none;
    }
    .drag-handle:active {
      cursor: grabbing;
    }
    .drag-handle-lock {
      display: none;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border-radius: 3px;
      cursor: pointer;
      color: #9ca3af;
      transition: color 0.15s, background-color 0.15s;
    }
    .drag-handle.show-lock .drag-handle-lock {
      display: flex;
    }
    .drag-handle-lock:hover {
      color: #6b7280;
      background-color: rgba(0, 0, 0, 0.05);
    }
    .drag-handle.is-locked .drag-handle-lock {
      color: #f59e0b;
    }
    .drag-handle.is-locked .drag-handle-lock:hover {
      color: #d97706;
      background-color: #fef3c7;
    }
    .drag-handle-grip {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border-radius: 3px;
      transition: background-color 0.15s;
    }
    .drag-handle-grip:hover {
      background-color: rgba(0, 0, 0, 0.05);
    }
    .drag-handle-grip svg {
      fill: #9ca3af;
      transition: fill 0.15s;
    }
    .drag-handle-grip:hover svg {
      fill: #6b7280;
    }
    .notes-editor .ProseMirror h1 {
      font-size: 15px;
      font-weight: 700;
      color: #111827;
      margin: 0 0 10px 0;
      padding-bottom: 6px;
      border-bottom: 2px solid #d1d5db;
    }
    .notes-editor .ProseMirror h2 {
      font-size: 11px;
      font-weight: 600;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      margin: 0 0 8px 0;
      padding-bottom: 4px;
      border-bottom: 1px solid #e5e7eb;
    }
    .notes-editor .ProseMirror .node-heading:not(:first-child) {
      margin-top: 24px;
    }
    .notes-editor .ProseMirror h3 {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
      margin: 12px 0 6px 0;
    }
    .notes-editor .ProseMirror p {
      margin: 4px 0;
      font-size: 13px;
      color: #374151;
    }
    .notes-editor .ProseMirror a {
      color: #2563eb;
      text-decoration: underline;
      overflow-wrap: anywhere;
      cursor: pointer;
    }
    .notes-editor .ProseMirror a:focus-visible {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }
    .notes-editor .ProseMirror ul {
      margin: 4px 0;
      padding-left: 20px;
    }
    .notes-editor .ProseMirror li {
      font-size: 13px;
      color: #374151;
      margin: 2px 0;
    }
    .notes-editor .ProseMirror li p {
      margin: 0;
    }
    /* List item styles */
    .notes-editor .list-item-wrapper.locked {
      background: #fffbeb;
      border-radius: 3px;
    }
    /* Placeholder extension styles */
    .notes-editor .ProseMirror p.is-empty::before {
      content: none !important;
    }
    .notes-editor .ProseMirror .node-heading + p.is-empty:last-child::before,
    .notes-editor .ProseMirror .node-heading + p.is-empty:has(+ .node-heading)::before {
      content: attr(data-placeholder) !important;
      color: #9ca3af;
      font-style: italic;
      font-size: 12px;
      pointer-events: none;
      height: 0;
      float: left;
    }
    .notes-editor .ProseMirror:focus {
      outline: none;
    }
    /* Highlight for new AI content */
    @keyframes ai-highlight {
      0% { background-color: #fef08a; }
      100% { background-color: transparent; }
    }
    .notes-editor .ai-new {
      animation: ai-highlight 1s ease-out;
    }
  `;
  document.head.appendChild(style);
};

// =============================================================================
// Main Component
// =============================================================================

interface NotesEditorProps {
  tree: TreeNode;
  onContentChange?: (json: JSONContent) => void;
  onToggleLock?: (nodeId: string, locked: boolean) => void;
}

export function NotesEditor({ tree, onContentChange, onToggleLock }: NotesEditorProps) {
  const lastContentRef = useRef("");
  const isExternalUpdateRef = useRef(false);
  const lastTypingTimeRef = useRef(0);
  const hoveredNodeRef = useRef<NodeChangeData | null>(null);

  useEffect(() => {
    injectStyles();
  }, []);

  // Handle node change from drag handle - update lock icon visibility
  const handleNodeChange = useCallback((data: NodeChangeData | null) => {
    const dragHandle = document.querySelector(".drag-handle");
    if (!dragHandle) return;

    if (data) {
      hoveredNodeRef.current = data;
      const isLocked = data.node.attrs?.locked || false;
      dragHandle.classList.add("show-lock");
      dragHandle.classList.toggle("is-locked", isLocked);
    } else {
      hoveredNodeRef.current = null;
      dragHandle.classList.remove("show-lock", "is-locked");
    }
  }, []);

  // Convert tree to TipTap content - use empty doc if no tree yet
  const initialContent = tree ? treeToTiptap(tree) : { type: "doc", content: [] };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: { openOnClick: true, HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" } },
        heading: false, // Disable default, use custom
        listItem: false, // Disable default, use custom
        paragraph: false, // Disable default, use custom
      }),
      CustomHeading.configure({
        levels: [1, 2, 3, 4],
      }),
      HideEmptySections,
      CustomListItem,
      CustomParagraph,
      Underline,
      UniqueID.configure({
        attributeName: "nodeId",
        types: ["heading", "paragraph", "bulletList", "listItem"],
        generateID: () => `node_${Math.random().toString(36).slice(2, 10)}`,
      }),
      Placeholder.configure({
        placeholder: "Write your notes here…",
        showOnlyWhenEditable: true,
        showOnlyCurrent: false,
        emptyNodeClass: "is-empty",
      }),
      GlobalDragHandle.configure({
        dragHandleWidth: 24,
        scrollTreshold: 100,
        dragHandleSelector: ".custom-drag-handle",
        fixedX: 16,
        onNodeChange: handleNodeChange,
      }),
    ],
    content: initialContent,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: "notes-editor",
      },
    },
    onUpdate: ({ editor }) => {
      // Don't trigger callback if this was an external update
      if (isExternalUpdateRef.current) {
        isExternalUpdateRef.current = false;
        return;
      }

      // Track when user last typed
      lastTypingTimeRef.current = Date.now();

      const json = editor.getJSON();
      const jsonStr = JSON.stringify(json);

      if (jsonStr !== lastContentRef.current) {
        lastContentRef.current = jsonStr;

        // Notify parent with TipTap JSON
        if (onContentChange) {
          onContentChange(json);
        }
      }
    },
  });

  // Handle lock icon click in drag handle
  const handleDragHandleLockClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!hoveredNodeRef.current || !editor) return;

      const { node, pos } = hoveredNodeRef.current;
      const nodeId = node.attrs?.nodeId as string | undefined;
      const newLocked = !node.attrs?.locked;

      editor
        .chain()
        .focus()
        .command(({ tr }) => {
          tr.setNodeAttribute(pos, "locked", newLocked);
          return true;
        })
        .run();

      // Update drag handle visual immediately
      const dragHandle = document.querySelector(".drag-handle");
      if (dragHandle) {
        dragHandle.classList.toggle("is-locked", newLocked);
      }

      // Notify parent
      if (onToggleLock && nodeId) {
        onToggleLock(nodeId, newLocked);
      }
    },
    [editor, onToggleLock]
  );

  // Sync external tree changes (from AI) into editor
  useEffect(() => {
    if (!editor || !tree) return;

    // Skip if user typed recently (within 2 seconds)
    const timeSinceTyping = Date.now() - lastTypingTimeRef.current;
    if (timeSinceTyping < 2000) return;

    const newContent = treeToTiptap(tree);
    const newContentStr = JSON.stringify(newContent);
    const currentContentStr = JSON.stringify(editor.getJSON());

    // Only update if content differs
    if (newContentStr !== currentContentStr) {
      isExternalUpdateRef.current = true;
      editor.commands.setContent(newContent);
      lastContentRef.current = newContentStr;
    }
  }, [editor, tree]);

  return (
    <div className="notes-editor-container">
      <EditorToolbar editor={editor} />
      <div style={styles.editor} className="notes-editor">
        <EditorContent editor={editor} />
        {/* Custom drag handle with lock icon */}
        <div className="custom-drag-handle drag-handle">
          <div className="drag-handle-lock" onClick={handleDragHandleLockClick}>
            <svg
              viewBox="0 0 24 24"
              width="12"
              height="12"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <div className="drag-handle-grip">
            <svg viewBox="0 0 10 12" width="10" height="12">
              <circle cx="2" cy="2" r="1.5" />
              <circle cx="8" cy="2" r="1.5" />
              <circle cx="2" cy="6" r="1.5" />
              <circle cx="8" cy="6" r="1.5" />
              <circle cx="2" cy="10" r="1.5" />
              <circle cx="8" cy="10" r="1.5" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotesEditor;
